import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import type { ReactNode } from "react";
import { Navigate, useNavigate, useParams } from "react-router";
import type { Project } from "@gb/contracts";
import { ErrorDialog } from "@/components/gb/error-dialog";
import { Stepper, type StepperStep } from "@/components/gb/stepper";
import { Button } from "@/components/ui/button";
import { useProjectContext } from "@/app/project-context";
import { cn } from "@/lib/utils";
import { StepBasics } from "./step-basics";
import { StepCompliance } from "./step-compliance";
import { StepMilestones } from "./step-milestones";
import { StepReview } from "./step-review";

/**
 * Each step's number, by name. Steps move to one another by name, so a step
 * added or taken out is changed here, and in `SETUP_STEP_COUNT` in
 * `@gb/contracts`, which bounds the saved cursor and is where activation puts
 * it.
 *
 * Four steps since 2026-10-03. Documents was step 3 and went at the owner's
 * call: files are added in one place, Brain → Sources, which the screen after
 * setup leads to (`product/0016`).
 */
export const STEP = { basics: 1, milestones: 2, compliance: 3, review: 4 } as const;

export const SETUP_STEPS: StepperStep[] = [
  { number: STEP.basics, label: "Basics" },
  { number: STEP.milestones, label: "Milestones" },
  { number: STEP.compliance, label: "Compliance" },
  { number: STEP.review, label: "Review" },
];

/**
 * Which of the two flows the wizard is running.
 *
 * Derived from `project.status`, not from a route or a prop drilled down from
 * the page: one source of truth, and a deep link to `/projects/:id/setup/3` on
 * a live project renders edit mode with no extra wiring. A parallel
 * `/projects/:id/edit/:step` tree would double the routing surface to express
 * one boolean.
 */
export type WizardMode = "setup" | "edit";

/** What every step component is handed. */
export type StepProps = {
  /** Null only at `/projects/new`, before the project has been created. */
  project: Project | null;
  /**
   * `edit` once the project is live.
   *
   * Every step reads this. Steps 1–3 skip `PUT /setup-step` in edit mode — the
   * cursor is monotonic and an active project is already at the last step, so
   * every such call is a no-op round trip. Step 1 also changes its Back,
   * Compliance admits its blast radius, and Review saves rather than
   * activating.
   */
  mode: WizardMode;
  /**
   * Move to a step. Navigation only — it writes nothing.
   *
   * This used to claim it wrote the server-side cursor. It never did: the
   * cursor is advanced by each step's own `useSetSetupStep()` call inside
   * `submit()`, and `goTo` is one `navigate()`. The comment was load-bearing in
   * the wrong direction — it made "jumping steps loses your edits" look
   * impossible from a reading of the type.
   */
  goTo: (step: number) => void;
  /**
   * Called instead of `goTo` when step 1 has just created the project.
   *
   * Only `/projects/new` supplies it: that route has no project id in the URL,
   * so it cannot build the path to step 2 until the server hands one back.
   */
  onCreated?: (project: Project) => void;
};

/**
 * The setup wizard: chrome, step routing and the guard that stops a user
 * deep-linking past what they have actually filled in.
 *
 * Progress lives on the server (`project.setupStep`), not in this component or
 * in storage, which is what makes "close the tab, come back tomorrow with the
 * permit" resume correctly — including on another machine.
 */
export function SetupWizard() {
  const { step: stepParam } = useParams();
  const navigate = useNavigate();
  // Non-null: `ProjectLayout` resolved the id and owns the pending, not-found
  // and failed states, so the wizard no longer carries a copy of each.
  const { project, projectId } = useProjectContext();

  const step = Number(stepParam);
  const base = `/projects/${projectId}`;

  // Edit mode is a property of the row, not of the URL. `active` is the only
  // status that reaches a step: `setup-incomplete` is still setting up, and an
  // archived project's page renders `EmptyState` and Unarchive, so it has no
  // entry point into the flow at all.
  const mode: WizardMode = project.status === "active" ? "edit" : "setup";

  // A step outside the wizard, or one past what the project has reached, falls
  // back to the cursor. Deep-linking to Review before giving basics would
  // render a summary of nothing and offer to activate it.
  //
  // This guard needs no edit-mode branch and must not grow one: `setupStep` is
  // the last step on every live project, so it already permits every step. It
  // reads like a gate that would block editing, and it is not.
  const furthest = project.setupStep;
  if (!Number.isInteger(step) || step < 1 || step > SETUP_STEPS.length) {
    return <Navigate to={`${base}/setup/${furthest}`} replace />;
  }
  if (step > furthest) {
    return <Navigate to={`${base}/setup/${furthest}`} replace />;
  }

  const goTo = (next: number): void => {
    navigate(next < 1 ? `${base}/overview` : `${base}/setup/${next}`);
  };

  return (
    <WizardColumn>
      <WizardHeader mode={mode} />
      {/* No `onSelect`: the rail reports progress, it does not navigate.
          Clicking a pill called `goTo`, which is a bare `navigate()` — the step
          being left unmounted with its `useState` uncommitted, so an edit made
          and then jumped away from was gone with nothing said. Only Continue
          writes. Traversal is linear, via Back and Continue. */}
      <Stepper
        steps={SETUP_STEPS}
        current={step}
        furthest={furthest}
        className="mb-[9px]"
      />
      {/* The design labels the current step once, under the rail, rather than
          captioning every pill. */}
      <div className="mb-[18px] font-display text-[0.8rem] font-bold uppercase tracking-[0.06em] text-ink">
        Step {step} of {SETUP_STEPS.length} · {SETUP_STEPS[step - 1]?.label}
      </div>
      <StepBody step={step} project={project} mode={mode} goTo={goTo} />
    </WizardColumn>
  );
}

/**
 * The design's wizard head: eyebrow, title, and a one-line subtitle. No hazard
 * stripe — that motif belongs to the sidebar and the Site Brain hero, and I had
 * added it here on my own initiative. `margin-bottom:22px` in the source.
 *
 * The three strings are the whole of the edit-mode chrome. The step indicator,
 * the step titles and the step order are all unchanged, deliberately: the rail
 * is the only thing that would tell a user "this is a different flow", and it
 * is not.
 */
export function WizardHeader({ mode = "setup" }: { mode?: WizardMode }) {
  const edit = mode === "edit";
  return (
    <div className="mb-[22px]">
      <div className="font-display text-[0.7rem] font-semibold uppercase tracking-[0.1em] text-brand">
        {edit ? "Project · Edit setup" : "Project · Setup"}
      </div>
      <h1 className="font-display text-[1.7rem] font-bold uppercase leading-[1.05] tracking-[0.01em] text-ink">
        {edit ? "Edit project setup" : "Set up project"}
      </h1>
      {/* The same line in both modes, per the design. It briefly read "Every
          change is saved as you make it." in edit mode — truthful, and it made
          step 3's promise easier to believe — but the design keeps the setup
          subtitle here, and step 3 explains the save model at the one point it
          actually matters. */}
      <p className="mt-1 text-[0.95rem] text-muted">
        Sets the world the rest of the app reads from.
      </p>
    </div>
  );
}

/**
 * The wizard's own column: 840px, not the app-wide 1180px.
 *
 * This is most of why every step used to run past the fold — a form laid out
 * across 1180px is far taller than the same form at 840px, because the panel
 * padding, the field grid and the type all scale off the column.
 */
export function WizardColumn({ children }: { children: ReactNode }) {
  return (
    <div className="mx-auto max-w-[840px] px-[30px] pt-7 pb-[60px]">{children}</div>
  );
}

/**
 * The panel every step's content sits in — the design's 14px-radius padded
 * card, with an optional title/description head.
 */
export function StepPanel({
  title,
  description,
  children,
  className,
}: {
  title: string;
  description?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        // 24px padding and the flat shadow, per the source. `shadow-card` was
        // my own substitution and reads far heavier than the design.
        "rounded-card-lg border-[1.5px] border-border bg-surface p-6 shadow-flat",
        className,
      )}
    >
      <h2 className="font-display text-[1.15rem] font-bold uppercase leading-[1.1] tracking-[0.01em] text-brand">
        {title}
      </h2>
      {description && (
        <p className="mt-1.5 text-[0.95rem] leading-[1.5] text-ink-prose">{description}</p>
      )}
      <div className="mt-[18px]">{children}</div>
    </section>
  );
}

/**
 * The shared Back/Continue footer.
 *
 * `hover:bg-brand-deep` rather than the default `brand-hover`: the design uses
 * the deeper orange on the wizard specifically, and the two are not
 * interchangeable (see the note in `ui/button.tsx`).
 *
 * `error` renders **twice**, and the duplication is the point.
 *
 * It used to be the banner alone. The banner sits below the panel, so on any
 * step tall enough to scroll — Milestones with six phases, Review with all four
 * sections — a failed Continue put its own explanation off-screen and the
 * button simply appeared dead. So a modal was added, which announces the
 * failure where the user is actually looking.
 *
 * The modal cannot replace the banner, though, which is what the first attempt
 * did and what Review caught. There, `error` is not a transient notification:
 * it is a *blocking* state, paired with `continueDisabled={Boolean(saveError)}`.
 * A dialog is dismissible by design, so dismissing it left a permanently
 * disabled Save button with nothing on screen saying why — the same
 * unexplained dead button, reintroduced one click later.
 *
 * Modal to notice it, banner to keep explaining it. Routed through here rather
 * than at each step, so every step got both without a call-site change.
 */
export function WizardFooter({
  onBack,
  backLabel = "Back",
  onContinue,
  continueLabel = "Continue",
  continueDisabled,
  busy,
  error,
}: {
  onBack?: () => void;
  backLabel?: string;
  onContinue: () => void;
  continueLabel?: string;
  continueDisabled?: boolean;
  busy?: boolean;
  error?: string | null;
}) {
  return (
    <div className="mt-5">
      <ErrorDialog error={error ?? null} />
      {error && (
        <p
          role="alert"
          className="mb-3 rounded-input border-[1.5px] border-danger/40 bg-danger-bg px-4 py-3 text-[0.85rem] font-semibold text-danger"
        >
          {error}
        </p>
      )}
      <div className="flex items-center justify-between gap-4">
        {onBack ? (
          <Button variant="secondary" onClick={onBack} type="button">
            <ArrowLeft size={16} />
            {backLabel}
          </Button>
        ) : (
          <span />
        )}
        <Button
          type="button"
          onClick={onContinue}
          disabled={continueDisabled || busy}
          className="hover:bg-brand-deep"
        >
          {busy ? <Loader2 size={16} className="animate-spin" /> : null}
          {continueLabel}
          {!busy && <ArrowRight size={16} />}
        </Button>
      </div>
      {/* Announced without stealing focus, so a slow save is perceivable to a
          screen reader user rather than silent. */}
      <span aria-live="polite" className="sr-only">
        {busy ? "Saving…" : ""}
      </span>
    </div>
  );
}

/** Dispatch to the step component. */
function StepBody({
  step,
  project,
  mode,
  goTo,
}: {
  step: number;
  project: Project | null;
  mode: WizardMode;
  goTo: (n: number) => void;
}) {
  switch (step) {
    case STEP.basics:
      return <StepBasics project={project} mode={mode} goTo={goTo} />;
    case STEP.milestones:
      return <StepMilestones project={project} mode={mode} goTo={goTo} />;
    case STEP.compliance:
      return <StepCompliance project={project} mode={mode} goTo={goTo} />;
    default:
      return <StepReview project={project} mode={mode} goTo={goTo} />;
  }
}
